import { Button } from '@/components/ui/button';
import { AnimatedBackground } from '@/components/ui/animated-background';
import { pages } from "@/Environment";
import { ArrowRight, ShieldCheck, LineChart, FileText, Lock } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';

const heroHighlights = [
  { icon: ShieldCheck, label: 'Your PDF is not stored after the analysis' },
  { icon: LineChart, label: 'An overall score, with your main strengths and gaps' },
  { icon: FileText, label: 'Professional reports ready to share' },
];

const trustMetrics = [
  { value: '48', label: 'Criteria in the current framework' },
  { value: '61', label: 'Criteria in the advanced framework' },
];

export function Hero() {
  const reduceMotion = useReducedMotion();

  return (
    <AnimatedBackground className="w-full">
      <section className="mx-auto flex min-h-[80vh] max-w-6xl flex-col gap-16 px-6 pb-24 pt-32 md:flex-row md:items-start md:justify-between">
        <motion.div
          className="space-y-10 md:w-7/12"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <span className="inline-flex items-center rounded-full border border-[rgba(31,76,84,0.18)] bg-white/70 px-4 py-2 text-sm font-semibold text-[var(--accent)] shadow-[0_12px_24px_rgba(15,23,42,0.05)]">
            Modern slavery intelligence
          </span>

          <div className="space-y-6">
            <h1 className="font-display text-balance text-4xl font-semibold leading-[1.15] !tracking-display text-[var(--text-primary)] md:text-6xl md:leading-[1.1]">
              Precision insight for modern slavery statements.
            </h1>

            <Link
              to="/trust"
              className="inline-flex items-center gap-2 rounded-lg border border-[rgba(31,76,84,0.15)] bg-white/60 px-3 py-2 text-xs transition hover:border-[rgba(31,76,84,0.25)] hover:bg-white/80"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[rgba(31,76,84,0.08)] text-[var(--accent)]">
                <Lock className="h-4 w-4" />
              </span>
              <div>
                <div className="font-semibold text-[var(--text-primary)]">Privacy-first AI, hosted in the UK and EU</div>
                <div className="text-[var(--text-secondary)]">
                  GDPR-compliant · UK and EU data residency · Your data is not used to train AI models
                </div>
              </div>
            </Link>

            <p className="max-w-xl text-lg leading-relaxed text-[var(--text-secondary)] md:text-xl">
              Replace manual review with an always-on analyst that surfaces evidence, benchmarks your performance, and guides your next compliance move with calm authority.
            </p>
          </div>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <Link to={pages.UPLOAD} className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto">
                Try analysis
                <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
            <Button
              size="lg"
              variant="outline"
              className="w-full sm:w-auto border-[rgba(31,76,84,0.18)]"
              onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' })}
            >
              View methodology
            </Button>
          </div>

          <ul className="grid gap-4 sm:grid-cols-3">
            {heroHighlights.map(({ icon: Icon, label }) => (
              <li
                key={label}
                className="flex items-start gap-3 rounded-2xl border border-[rgba(31,76,84,0.12)] bg-white/80 p-4 shadow-[0_16px_32px_rgba(15,23,42,0.06)]"
              >
                <span className="mt-1 flex h-8 w-8 items-center justify-center rounded-xl bg-[rgba(31,76,84,0.08)] text-[var(--accent)]">
                  <Icon className="h-4 w-4" />
                </span>
                <p className="text-sm font-medium leading-snug text-[var(--text-primary)]">{label}</p>
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.aside
          className="flex w-full flex-col gap-6 rounded-3xl border border-[rgba(31,76,84,0.12)] bg-white/80 p-8 shadow-[0_28px_56px_rgba(15,23,42,0.08)] md:max-w-sm"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.8, ease: 'easeOut' }}
        >
          <div className="space-y-2">
            <p className="eyebrow-muted">
              Immediate clarity
            </p>
            <p className="text-sm leading-relaxed text-[var(--text-secondary)]">
              Upload a statement and get a scored report with strengths, priority improvements and recommended next steps.
            </p>
          </div>

          <dl className="grid gap-4">
            {trustMetrics.map(({ value, label }) => (
              <div key={label} className="rounded-2xl border border-[rgba(31,76,84,0.08)] bg-[var(--accent-light)]/70 px-5 py-4">
                <dt className="text-sm text-[var(--text-secondary)]">{label}</dt>
                <dd className="mt-1 font-display text-3xl font-semibold !tracking-display text-[var(--text-primary)]">{value}</dd>
              </div>
            ))}
          </dl>

        </motion.aside>
      </section>
    </AnimatedBackground>
  );
}
